/**
 * The FoldMyCard API, typed. Same origin, so the session cookie just goes along.
 *
 * Pictures and cards are not on the server: since 2026-10-10 they are kept on
 * this device (local/store.ts), behind the same calls they had when they were.
 */
import type { FoldGuide, PrintGuides, ProjectDocument, TemplateDocument, ZoneShapes } from '@foldmycard/scene';
import { ApiError } from './api-error.ts';
import * as local from './local/store.ts';
import { versionToUse } from './template-versions.ts';

export { ApiError };

async function call<T>(path: string, init: RequestInit & { json?: unknown } = {}): Promise<T> {
  const { json, ...rest } = init;
  const res = await fetch(`/api/v1${path}`, {
    credentials: 'same-origin',
    ...rest,
    headers: json !== undefined ? { 'content-type': 'application/json' } : undefined,
    body: json !== undefined ? JSON.stringify(json) : rest.body,
  });
  const text = await res.text();
  const body = text ? JSON.parse(text) : null;
  if (!res.ok) throw new ApiError(res.status, body || {});
  return body as T;
}

export interface Size { width: number; height: number }
export interface ImageItem {
  id: string;
  name: string | null;
  width: number;
  height: number;
  createdAt: string;
  sizes: { thumb: Size; preview: Size };
  /** blob: addresses of the copies kept on this device (local/store.ts). */
  urls: { thumb: string; preview: string };
  /** The cartoon look of this picture, once one has been made (local/cartoon.ts). */
  cartoon: { sizes: { thumb: Size; preview: Size }; urls: { thumb: string; preview: string } } | null;
  /** The black-and-white look, once made (local/pictures.ts). */
  bw: { sizes: { thumb: Size; preview: Size }; urls: { thumb: string; preview: string } } | null;
  /** The card it was added to; null when added from the home page (or before cards kept their own). */
  projectId: string | null;
  /** The library drawing it was made from, if any. */
  library: string | null;
  /** The picture of another card it was copied from (its first), if any. */
  copiedFrom?: string | null;
}
/** `suspended`: access cancelled from the admin portal; the account and its cards are kept. */
export type Approval = 'pending' | 'approved' | 'declined' | 'suspended';
export interface Me {
  id: string; email: string; name: string;
  /** Whether this account may use the app yet (approval step, apps/api/src/approval.ts). */
  approval: Approval;
  theme: string;
  /** Receives the sign-up emails and may approve accounts. */
  approver: boolean;
  /** The Terms and Disclaimer: the current version, and whether this account has accepted it. */
  terms: { version: string; effectiveDate: string; accepted: boolean; acceptedVersion: string | null; acceptedAt: string | null };
}
export interface LegalInfo {
  version: string; effectiveDate: string; legalName: string;
  documents: { terms: string; disclaimer: string; privacy: string };
}
/** Where things are, when idle sign-out comes, and where to leave a tip (GET /api/v1/site - public). */
export interface Site {
  appUrl: string; adminUrl: string | null; idleMinutes: number; adminIdleMinutes: number; supportUrl?: string | null;
  /** Paused from the admin portal: the app shows only a note (paused.tsx). */
  paused?: boolean; pausedMessage?: string | null;
  signUpsOpen?: boolean;
  /** New accounts wait for an administrator (off since 2026-10-19 unless switched on). */
  approvalRequired?: boolean;
}
/** The kinds of event an invitation can be for (its details stay on the device: local/events.ts). */
export const EVENT_KINDS = ['birthday', 'wedding', 'baby-shower', 'graduation', 'party', 'dinner', 'holiday', 'other'] as const;
export type EventKind = typeof EVENT_KINDS[number];
/**
 * An event as the server has it (apps/api/src/events.ts, since 2026-10-24): no name, date, address
 * or contact - only until when to keep it, whether it is in the archive, and whether details from
 * before are still there for this device to take (`legacy`).
 */
export interface ServerEvent { id: string; keepUntil: string | null; deletedAt: string | null; legacy: boolean }
export type RsvpAnswer = 'yes' | 'no' | 'maybe';
/** A guest's code and answer. Their name is on the host's device (local/events.ts). */
export interface Guest {
  id: string; link: string; response: RsvpAnswer | null; partySize: number | null; message: string | null; respondedAt: string | null;
  /** The card this guest gets (an event can have several, 2026-10-23). */
  projectId: string | null;
}
export interface EventSummary { guests: number; yes: number; no: number; maybe: number; waiting: number; coming: number }
/** One of an event's cards (its id; the card itself is on the host's device) and how many guests it is for. */
export interface EventCard { projectId: string; guests: number }
export interface EventFull { event: ServerEvent; guests: Guest[]; summary: EventSummary; cards: EventCard[] }
export interface EventBrief { event: ServerEvent; summary: EventSummary; cards: EventCard[] }
/** An event from before 2026-10-24, as the server still had it: for this device to keep. */
export interface LegacyEvent {
  details: { title: string; kind: EventKind; startsOn: string; startsAt: string | null; timeZone: string | null; place: string; contact: string; note: string };
  names: Record<string, string>;
}
/** What a guest's code opens: who the host is, and the guest's answer. */
export interface RsvpView {
  host: string | null;
  guest: { response: RsvpAnswer | null; partySize: number | null; message: string | null; respondedAt: string | null };
}

/** The site controls in the admin portal (apps/api/src/site-settings.ts). */
export interface SiteControls {
  approvalRequired: boolean; signUpsOpen: boolean; notifySignUps: boolean; notifySignIns: boolean;
  paused: boolean; pausedMessage: string;
}

/* the admin portal (apps/api/src/admin.ts) */
export interface AdminUser {
  id: string; name: string; email: string; emailVerified: boolean; approval: Approval; approver: boolean;
  decidedAt: string | null; createdAt: string; lastSignInAt: string | null; lastSeenAt: string | null;
  /** The promo code given at sign-up - or, once in, the one that let them in (apps/api/src/promo.ts). */
  promoCode: string | null;
}
/** A promo code (apps/api/src/promo.ts): lets new accounts in without waiting for approval. */
export interface PromoCode {
  code: string; note: string; maxUses: number | null; uses: number; expiresAt: string | null; active: boolean;
  createdBy: string | null; createdAt: string;
  /** It would let someone in now: on, not past its last day, not used up. */
  usable: boolean;
}
export interface AdminLogEntry {
  at: string; by: string; action: string; detail: Record<string, unknown> | null;
  targetId?: string | null; targetEmail?: string | null;
}
export interface AdminUserDetail {
  user: AdminUser & { theme: string; termsVersion: string | null; termsAcceptedAt: string | null; termsCurrent: boolean };
  sessions: { id: string; createdAt: string; lastUsedAt: string; expiresAt: string; ip: string | null; userAgent: string | null }[];
  log: AdminLogEntry[];
}
export interface AdminOverview {
  accounts: { total: number; waiting: number; unconfirmed: number; active: number; declined: number; suspended: number; admins: number };
  signUps: { last7: number; last30: number };
  activeLast7: number;
  subscriptions: { enabled: boolean };
}

/** A photograph of the picture library, added by the administrator (apps/api/src/library.ts). */
export interface LibraryPhoto {
  id: string; group: string; name: string; width: number; height: number; bytes: number; createdAt: string;
  urls: { preview: string; thumb: string };
}

export interface ProjectItem {
  id: string; title: string; templateId: string; paper: string;
  createdAt: string; updatedAt: string; versions?: number;
}
export interface VersionItem {
  id: string; label: string; basedOn: string | null; revision: number; templateHash: string;
  createdAt: string; updatedAt: string; document?: ProjectDocument;
}

/** The current version of a card design. */
const latestTemplate = (id: string, paper: string) => call<TemplateDocument>(`/templates/${id}/${paper}`);

export const api = {
  latestTemplate,
  // a card made on a withdrawn version is served its correction (template-versions.ts)
  template: (id: string, paper: string, hash: string) =>
    call<TemplateDocument>(`/templates/${id}/${paper}/${versionToUse(id, paper, hash)}`),
  /** Fold guides for that template version; null when it has none. */
  guides: (id: string, paper: string, hash: string) =>
    // ?v: a new address whenever a version's guides are rewritten, past what browsers keep for an hour
    // (2: the twisted purse's lines numbered 1, 2, 3, 5 by the step that creases them)
    call<PrintGuides>(`/templates/${id}/${paper}/${versionToUse(id, paper, hash)}/guides?v=2`).catch(() => null),
  /** The other shapes zones of that template version can take; null when it has none. */
  shapes: (id: string, paper: string, hash: string) =>
    // ?v: a new address whenever browsers may hold a list that must not be used (3: zone 10's big half heart
    // came numbered 3 in v2); the server no longer lets them keep one (no-cache)
    call<ZoneShapes>(`/templates/${id}/${paper}/${versionToUse(id, paper, hash)}/shapes?v=3`).catch(() => null),
  /** The illustrated folding guide for that template version; null when it has none. */
  folding: (id: string, paper: string, hash: string) =>
    // ?v: as for the guides (2: the twisted purse in eleven steps, 2026-10-18)
    call<FoldGuide>(`/templates/${id}/${paper}/${versionToUse(id, paper, hash)}/folding?v=2`).catch(() => null),

  /* pictures, on this device */
  images: local.images,
  upload: local.upload,
  copyPictures: local.copyPictures,
  deleteImage: local.deleteImage,
  /** Make (or fetch the already made) cartoon look of a picture: a few seconds the first time. */
  cartoon: local.cartoon,
  blackWhite: local.blackWhite,
  addFromLibrary: local.addFromLibrary,

  /* cards, on this device */
  projects: local.projects,
  createProject: async (title: string, templateId = 'classic', paper = 'usletter') =>
    local.createProject(title, await latestTemplate(templateId, paper)),
  /** The card designs the server has, by id (apps/api/src/templates.ts). */
  templates: () => call<{ templates: { id: string; name: string }[] }>('/templates'),
  project: local.project,
  renameProject: local.renameProject,
  deleteProject: local.deleteProject,

  version: local.version,
  saveVersion: local.saveVersion,
  deleteVersion: local.deleteVersion,
  duplicateVersion: local.duplicateVersion,
  renameVersion: local.renameVersion,
  nextVersionLabel: local.nextLabel,

  deleteAccount: (confirmEmail: string) => call<null>('/me', { method: 'DELETE', json: { confirmEmail } }),
  me: () => call<Me>('/me'),
  legal: () => call<LegalInfo>('/legal'),
  /** Contact us (contact.tsx): public, emailed on by the server. `website` is the robots' field. */
  contact: (m: { email: string; subject: string; message: string; website: string }) =>
    call<{ ok: true }>('/contact', { method: 'POST', json: m }),
  acceptTerms: (version: string) => call<{ version: string; accepted: boolean }>('/me/terms', { method: 'POST', json: { version } }),
  saveSettings: (theme: string) => call<{ theme: string }>('/me/settings', { method: 'PUT', json: { theme } }),
  /** A promo code, from the waiting page: lets the account in at once. */
  redeemPromo: (code: string) => call<{ approval: Approval }>('/me/promo', { method: 'POST', json: { code } }),

  site: () => call<Site>('/site'),

  /* invitations with RSVP (apps/api/src/events.ts) */
  events: () => call<{ events: EventBrief[] }>('/events'),
  /** Deleted events, kept for 30 days (`purgeOn`). */
  archivedEvents: () => call<{ events: (EventBrief & { purgeOn: string })[] }>('/events/archive'),
  event: (id: string) => call<EventFull>(`/events/${encodeURIComponent(id)}`),
  /** A new event: the server is told only until when to keep it, and its first card. */
  createEvent: (keepUntil: string, projectId: string | null) => call<EventFull>('/events', { method: 'POST', json: { keepUntil, projectId } }),
  /** The event's date moved, and with it the day the server deletes it. */
  updateEvent: (id: string, keepUntil: string) => call<EventFull>(`/events/${encodeURIComponent(id)}`, { method: 'PUT', json: { keepUntil } }),
  legacyEvent: (id: string) => call<LegacyEvent>(`/events/${encodeURIComponent(id)}/legacy`),
  /** This device has the event's details: the server empties its old copy. */
  forgetEvent: (id: string) => call<EventFull>(`/events/${encodeURIComponent(id)}/forget`, { method: 'POST' }),
  /** Into the archive: restoreEvent brings it back. */
  deleteEvent: (id: string) => call<null>(`/events/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  restoreEvent: (id: string) => call<EventFull>(`/events/${encodeURIComponent(id)}/restore`, { method: 'POST' }),
  deleteEventForever: (id: string) => call<null>(`/events/${encodeURIComponent(id)}/forever`, { method: 'DELETE' }),
  /** Another card for the event. */
  addEventCard: (id: string, projectId: string) => call<EventFull>(`/events/${encodeURIComponent(id)}/cards`, { method: 'POST', json: { projectId } }),
  /** `count` new guests on a card: their ids come back in order (`added`), for this device to name. */
  addGuests: (id: string, count: number, projectId?: string) =>
    call<EventFull & { added: string[] }>(`/events/${encodeURIComponent(id)}/guests`, { method: 'POST', json: { count, projectId } }),
  moveGuest: (id: string, guest: string, projectId: string) =>
    call<EventFull>(`/events/${encodeURIComponent(id)}/guests/${encodeURIComponent(guest)}`, { method: 'PATCH', json: { projectId } }),
  removeGuest: (id: string, guest: string) =>
    call<EventFull>(`/events/${encodeURIComponent(id)}/guests/${encodeURIComponent(guest)}`, { method: 'DELETE' }),
  rsvp: (token: string) => call<RsvpView>(`/rsvp/${encodeURIComponent(token)}`),
  answerRsvp: (token: string, answer: { response: RsvpAnswer; partySize?: number; message?: string }) =>
    call<RsvpView>(`/rsvp/${encodeURIComponent(token)}`, { method: 'POST', json: answer }),

  /* the admin portal */
  adminMe: () => call<{ email: string; name: string; idleMinutes: number; appUrl: string; adminUrl: string | null }>('/admin/me'),
  adminOverview: () => call<AdminOverview>('/admin/overview'),
  adminSettings: () => call<{ settings: SiteControls }>('/admin/settings'),
  saveAdminSettings: (patch: Partial<SiteControls>) =>
    call<{ settings: SiteControls; letIn: number }>('/admin/settings', { method: 'PUT', json: patch }),
  adminUsers: () => call<{ users: AdminUser[] }>('/admin/users'),
  adminUser: (id: string) => call<AdminUserDetail>(`/admin/users/${encodeURIComponent(id)}`),
  setApproval: (id: string, approval: Approval) =>
    call<{ approval: Approval }>(`/admin/users/${encodeURIComponent(id)}/approval`, { method: 'POST', json: { approval } }),
  adminSignOut: (id: string) => call<{ signedOut: number }>(`/admin/users/${encodeURIComponent(id)}/sign-out`, { method: 'POST', json: {} }),
  adminPasswordReset: (id: string) =>
    call<{ sent: boolean; to: string }>(`/admin/users/${encodeURIComponent(id)}/password-reset`, { method: 'POST', json: {} }),
  adminResendConfirmation: (id: string) =>
    call<{ sent: boolean; to: string }>(`/admin/users/${encodeURIComponent(id)}/verification`, { method: 'POST', json: {} }),
  adminDeleteUser: (id: string, confirmEmail: string) =>
    call<null>(`/admin/users/${encodeURIComponent(id)}`, { method: 'DELETE', json: { confirmEmail } }),
  adminLog: (limit = 100) => call<{ entries: AdminLogEntry[] }>(`/admin/log?limit=${limit}`),
  backups: () => call<{ backups: string[] }>('/admin/backups'),
  adminPromoCodes: () => call<{ codes: PromoCode[] }>('/admin/promo-codes'),
  adminCreatePromo: (p: { code?: string; note?: string; maxUses?: number; expiresAt?: string }) =>
    call<PromoCode>('/admin/promo-codes', { method: 'POST', json: p }),
  adminSetPromoActive: (code: string, active: boolean) =>
    call<PromoCode>(`/admin/promo-codes/${encodeURIComponent(code)}/active`, { method: 'POST', json: { active } }),
  adminDeletePromo: (code: string) => call<null>(`/admin/promo-codes/${encodeURIComponent(code)}`, { method: 'DELETE' }),
  /** The picture library's photographs, for the editor. */
  library: () => call<{ items: LibraryPhoto[] }>('/library'),
  adminLibrary: () => call<{ items: LibraryPhoto[] }>('/admin/library'),
  adminAddLibrary: (form: FormData) => call<LibraryPhoto>('/admin/library', { method: 'POST', body: form }),
  adminDeleteLibrary: (id: string) => call<null>(`/admin/library/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  makeBackup: () => call<{ name: string; rows: Record<string, number>; bytes: number }>('/admin/backups', { method: 'POST', json: {} }),
  backupUrl: (name: string) => `/api/v1/admin/backups/${encodeURIComponent(name)}`,
  /** The approval link from the approver's email: no sign-in needed. */
  approvalLink: (token: string) =>
    call<{ user: { name: string; email: string; approval: Approval; createdAt: string } }>(`/approvals/${encodeURIComponent(token)}`),
  decideLink: (token: string, approval: 'approved' | 'declined') =>
    call<{ approval: Approval }>(`/approvals/${encodeURIComponent(token)}`, { method: 'POST', json: { approval } }),

};
